import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { StandardDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useScStore } from "../../../../../../store/scStandard.store";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";


interface StandardDetailsFormProps {
  standardData: StandardDetails[];
  setStandardData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  scStatus?: string; // Add SC Status prop
  ccsFlag?: string; // Add CCS Flag prop
  scValidFromDate?: string; // Add SC Valid From Date prop
}

const StandardDetailsForm: React.FC<StandardDetailsFormProps> = ({
  standardData,
  setStandardData,
  scStatus,
  ccsFlag,
  scValidFromDate,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [data, setData] = useState<StandardDetails[]>(standardData);
  const [tableData, setTableData] = useState<StandardDetails[]>(standardData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [withdrawalReasonOptions, setWithdrawalReasonOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { notify } = useNotification();
  const { setScStandards } = useScStore();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  // Dynamic validation schema based on SC Status
  const getValidationSchema = (scStatus?: string) => {
    return Yup.object().shape({
      scStandard: Yup.string().required("SC Standard is required"),
      scStandardDescription: Yup.string(),
      scStandardVersion: Yup.string().nullable(),
      scNumberOfFarms: Yup.number()
        .when('scStandardDescription', {
          is: (value: string) => ['Responsible Down Standard (RDS)', 'Responsible Mohair Standard (RMS)', 'Responsible Alpaca Standard (RAS)', 'Responsible Wool Standard (RWS)'].includes(value),
          then: (schema) => schema.required("SC Number of Farms is required for RDS, RMS, RAS, and RWS standards").min(0, "Number of Farms must be non-negative"),
          otherwise: (schema) => schema.optional().min(0, "Number of Farms must be non-negative")
        }),
      scStandardAddedDate: Yup.string()
        .required("Standard Added Date is required")
        .test('standard-added-date-sequence', 'Standard Added Date cannot be before SC Valid From Date',
          function(value) {
            if (!value || !scValidFromDate) return true;
            return new Date(value) >= new Date(scValidFromDate);
          }),
      scStandardRemovedDate: Yup.string()
        .test('removed-date-sequence', 'Standard Removed Date must be on or after Standard Added Date',
          function(value) {
            const addedDate = this.parent.scStandardAddedDate;
            if (!addedDate || !value) return true;
            return new Date(addedDate) <= new Date(value);
          })
        .when([], {
          is: () => scStatus === 'W',
          then: (schema) => schema.required("Standard Removed Date is required when SC Status is Withdrawn"),
          otherwise: (schema) => schema.optional()
        }),
      scStandardWithdrawalReason: Yup.array()
        .when('scStandardRemovedDate', {
          is: (value: string) => value && value.trim() !== '',
          then: (schema) => schema.min(1, "Standard Withdrawal Reason is required when Standard Removed Date is filled"),
          otherwise: (schema) => schema.optional()
        })
        .when([], {
          is: () => scStatus === 'W',
          then: (schema) => schema.required("Standard Withdrawal Reason is required when SC Status is Withdrawn"),
          otherwise: (schema) => schema.optional()
        }),
      scCertifiedOrganizationApprovedClaims: Yup.string().required("SC Certified Organization Approved Claims is required"),

    });
  };

  const validationSchema = getValidationSchema(scStatus);

  const scStandardValues: OptionType[] = [
    { label: "Organic Content Standard (OCS)", value: "Organic Content Standard (OCS)", code: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard (GRS)", value: "Global Recycled Standard (GRS)", code: "GRS" }, // Represents "Expired" 
    { label: "Recycled Claim Standard (RCS)", value: "Recycled Claim Standard (RCS)", code: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard (RDS)", value: "Responsible Down Standard (RDS)", code: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard (RWS)", value: "Responsible Wool Standard (RWS)", code: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard (RMS)", value: "Responsible Mohair Standard (RMS)", code: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard (RAS)", value: "Responsible Alpaca Standard (RAS)", code: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard (CCS)", value: "Content Claim Standard (CCS)", code: "CCS" }, // Represents "Withdrawn"
    // ...ccsFlag==="Yes"?[]:[{ label: "Sustainable Fiber Alliance (SFA)", value: "SFA (SFA)", code: "SFA" }], // Represents "Withdrawn"
    // { label: "Global Organic Textile Standard (GOTS)", value: "Global Organic Textile Standard (GOTS)", code: "GOTS" }, // Represents "Withdrawn"
  ];

  // Hardcoded withdrawal reason values - replaced with DTrackIt API
  // const standardWithdrawalReasonValues: OptionType[] = [
  //   { label: "Critical non-conformity", value: "Critical non-conformity", code: "C" },
  //   { label: "Non-conformity", value: "Non-conformity", code: "N" },
  //   { label: "Recertification completed", value: "Recertification completed", code: "R" },
  //   { label: "Withdrawal of the certification body's accreditation", value: "Withdrawal of the certification body's accreditation", code: "W" },
  //   { label: "Administrative", value: "Administrative", code: "A" },
  //   { label: "Voluntary", value: "Voluntary", code: "V" },
  //   { label: "Error", value: "Error", code: "E" },
  //   { label: "Other", value: "Other", code: "O" },
  // ];

  // Fetch withdrawal reason values from DTrackIt API
  const getWithdrawalReasonValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getStandardWithdrawalReasonValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
          code: item.code_1
        }));
        setWithdrawalReasonOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching withdrawal reason values:', error);
      notify('Failed to load withdrawal reason options', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<StandardDetails>({
    initialValues: {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scNumberOfFarms: 0,
      scStandardAddedDate: "",
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
      scCertifiedOrganizationApprovedClaims: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scStandardWithdrawalReason') {
      // Handle array of selected values for multi-select
      const selectedValues = Array.isArray(value) ? value : [];
      const selectedDescriptions = selectedValues.map(val => 
        withdrawalReasonOptions.find(option => option.value === val)?.label || val
      ).join(', ');
      
      formik.setFieldValue('scStandardWithdrawalReason', selectedValues);
      formik.setFieldValue('scStandardWithdrawalReasonDescription', selectedDescriptions);
        setData((prevData) =>
          prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scStandardWithdrawalReason']: selectedValues, 'scStandardWithdrawalReasonDescription': selectedDescriptions } : row
          )
        );
    } else if(columnId === 'scStandardDescription') {
      let optionCode = scStandardValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scStandardDescription', value);
      formik.setFieldValue('scStandard', optionCode);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, ['scStandardDescription']: value, 'scStandard': optionCode } : row
          )
        );
    } else if(columnId === 'scStandardRemovedDate') {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
      // Reset withdrawal reason if removed date is cleared
      if (!value || value.trim() === '') {
        formik.setFieldValue('scStandardWithdrawalReason', []);
        formik.setFieldValue('scStandardWithdrawalReasonDescription', '');
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, 'scStandardWithdrawalReason': [], 'scStandardWithdrawalReasonDescription': '' } : row
          )
        );
      }
        } else if(columnId === 'scCertifiedOrganizationApprovedClaims') {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
    
  };

  const handleAddNewRow = () => {
    const newRow: StandardDetails = {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scNumberOfFarms: 0,
      scStandardAddedDate: scValidFromDate || "", // Default to scValidFromDate if available
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
      scCertifiedOrganizationApprovedClaims: "",
    };
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null) {
      notify('No row is being edited', 'error');
      return;
    }

    // Validate the current row data
    const currentRowData = data[editingRowIndex];
    const hasRemovedDate = currentRowData?.scStandardRemovedDate && currentRowData.scStandardRemovedDate.trim() !== '';
    const hasWithdrawalReason = currentRowData?.scStandardWithdrawalReason && Array.isArray(currentRowData.scStandardWithdrawalReason) && currentRowData.scStandardWithdrawalReason.length > 0;
    
    // Check if withdrawal reason is required but missing
    if (hasRemovedDate && !hasWithdrawalReason) {
      notify('Standard Withdrawal Reason is required when Standard Removed Date is filled', 'error');
      return;
    }

    // Validate formik form
    if (!formik.isValid) {
      notify('Enter the mandatory fields', 'error');
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);

    const allStandards = data.map(row => row.scStandard);
    setScStandards(allStandards);
    setRestrictPopUpSave(false);
    setStandardData(data); // Update parent state
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  useEffect(() => {
    const currentStore = useScStore.getState();
    
    // Fetch withdrawal reason values from DTrackIt API
    getWithdrawalReasonValues();

    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  const columns: EditableColumnDef<StandardDetails>[] = [
    {
      accessorKey: "scStandardDescription",
      header: "SC Standard",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scStandardDescription"
          value={value}
          placeholder="Select SC Standard"
          options={scStandardValues}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "scStandardVersion",
      header: "SC Standard Version",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scStandardVersion"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC Standard Version"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scNumberOfFarms",
      header: "SC Number of Farms",
      Header: () => {
        // Check if any row in the table contains one of the required standards
        const hasRequiredStandard = data && Array.isArray(data) && data.some(row => 
          row && row.scStandardDescription && ['Responsible Down Standard (RDS)', 'Responsible Mohair Standard (RMS)', 'Responsible Alpaca Standard (RAS)', 'Responsible Wool Standard (RWS)'].includes(row.scStandardDescription)
        );
        
        return (
          <span>
            {hasRequiredStandard && <i style={{ color: 'red' }}>* </i>}
            SC Number of Farms
          </span>
        );
      },
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scNumberOfFarms"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
              e.preventDefault();
            }
          }}
          placeholder="Enter Number of Farms"
          type="number"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scStandardAddedDate",
      header: "Standard Added Date",
      Header: <span> <i style={{ color: 'red' }}>* </i>Standard Added Date</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scStandardAddedDate"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Standard Added Date"
          type="date"
          inputProps={{ 
            min: scValidFromDate || undefined 
          }}
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scStandardRemovedDate",
      header: "Standard Removed Date",
      Header: scStatus === "W" ? <span> <i style={{ color: 'red' }}>* </i>Standard Removed Date</span> : "Standard Removed Date",
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomTextField
          name="scStandardRemovedDate"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Standard Removed Date"
          type="date"
          inputProps={{ min: data[rowIndex]?.scStandardAddedDate || undefined }}
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scStandardWithdrawalReason",
      header: "Standard Withdrawal Reason",
      Header: (scStatus === "W" || data.some(row => row.scStandardRemovedDate && row.scStandardRemovedDate.trim() !== '')) 
        ? <span> <i style={{ color: 'red' }}>* </i>Standard Withdrawal Reason</span> 
        : "Standard Withdrawal Reason",
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="scStandardWithdrawalReason"
          value={value || []}
          placeholder="Select Withdrawal Reason"
          options={withdrawalReasonOptions}
          multiple={true}
          disabled={!data[rowIndex]?.scStandardRemovedDate || data[rowIndex]?.scStandardRemovedDate.trim() === ''}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
          },
      {
        accessorKey: "scCertifiedOrganizationApprovedClaims",
        header: "SC Certified Organization Approved Claims",
        Header: <span> <i style={{ color: 'red' }}>* </i>SC Certified Organization Approved Claims</span>,
        renderEditCell: ({ value, onChange }) => (
          <CustomSelect
            name="scCertifiedOrganizationApprovedClaims"
            value={value}
            placeholder="Select SC Certified Organization Approved Claims"
            options={[
              { label: "Product Claims (PC)", value: "PC", code: "PC" },
              { label: "Certified Organization Claims (CC)", value: "CC", code: "CC" },
              { label: "Both Product and Organization Claims (BOTH)", value: "BOTH", code: "BOTH" },
              { label: "No Claims Approved (NONE)", value: "NONE", code: "NONE" },
            ]}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
        ),
      },

  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setStandardData(updatedData); // Update parent with updated data
    const remainingStandards = updatedData.map(row => row.scStandard);

    setScStandards(remainingStandards);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || hasSubmitSC || isRestrictPopUpSave}>
            Add Standard Details
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitSC}
        />
      </Box>
    </div>
  );
};

export default StandardDetailsForm;
